import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Box, Grid, Tabs, Tab } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSTextField, RMSSelect, RMSCheckbox } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchAddressAutoComplete from '../../../shared/components/modules/SearchAddressAutoComplete';
import AddAddressDialog from './AddAddressDialog';
import PlaceAddressesTab from './PlaceAddressesTab';
import PlaceContactsTab from './PlaceContactsTab';
import PlaceEmployeesTab from './PlaceEmployeesTab';
import PlaceSopsTab from './PlaceSopsTab';
import PlaceVehicleRegistrationsTab from './PlaceVehicleRegistrationsTab';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import SectionLabel from './SectionLabel';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import {
  createPlace,
  getPlaceDetails,
  updatePlaceDetails,
  getPlaceSops,
} from '../../services/userService';
import addPlaceSchema from '../../schema/addPlaceSchema';
import {
  DICT_SELECT_PROPS,
  DEFAULT_VALUES,
  PLACE_TABS,
  PLACE_TAB_KEYS,
} from '../../constants/addPlaceFormConstants';

const AddPlaceDialog = ({ open, onClose, placeId = null, onPlaceCreated }) => {
  const dispatch = useDispatch();

  const isEditMode = Boolean(placeId);
  const placePerms = getFullPermissions('globals', 'Places', 'any');
  const canSavePlace = isEditMode ? placePerms.Edit : true;
  const isViewOnly = isEditMode && !placePerms.Edit;

  const [activeTab, setActiveTab] = useState(PLACE_TAB_KEYS.EDIT);
  const [sopsCount, setSopsCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPlace, setLoadingPlace] = useState(false);
  const [addAddressDialogOpen, setAddAddressDialogOpen] = useState(false);

  const methods = useForm({
    resolver: CreateFormResolver(addPlaceSchema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
    disabled: isViewOnly,
  });

  const { control, setValue } = methods;
  const { isDirty } = useFormState({ control });
  const [ptsAddressID, ContactMethod] = useWatch({
    control,
    name: ['ptsAddressID', 'ContactMethod'],
  });

  const { placeTypeOptions, contactMethodOptions, contactTypeOptions } = useUserDictionaryOptions();
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadPlaceDetails = useCallback(() => {
    if (!open || !placeId) return;

    executeApiAction({
      setLoading: setLoadingPlace,
      dispatch,
      action: () => getPlaceDetails(placeId),
      onSuccess: (place) => {
        if (!place) return;
        methods.reset({
          ...DEFAULT_VALUES,
          PlaceName: place.PlaceName || '',
          PlaceORI: place.PlaceORI || '',
          PlaceDepartmentID: place.PlaceDepartmentID || '',
          PlaceType: place.PlaceType || '',
          IsDestination: Boolean(place.IsDestination),
        });
      },
    });
  }, [open, placeId, dispatch, methods]);

  const loadSopsCount = useCallback(() => {
    if (!open || !placeId) return;

    executeApiAction({
      dispatch,
      action: () => getPlaceSops(placeId),
      onSuccess: (sopLinks) => setSopsCount((sopLinks || []).length),
    });
  }, [open, placeId, dispatch]);

  const renderTabLabel = (tab) => {
    if (tab.key === PLACE_TAB_KEYS.SOPS && sopsCount > 0) {
      return <span className="rms-tab-count-alert">{`${tab.label} (${sopsCount})`}</span>;
    }

    return tab.label;
  };

  const handleContactMethodChange = useCallback(() => {
    setValue('ContactMethodType', '');
    setValue('Info', '');
  }, [setValue]);

  const handleAddressCreated = useCallback(
    (address) => {
      setValue('ptsAddressID', address?.ptsAddressID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddAddressDialogOpen(false);
    },
    [setValue]
  );

  const onSubmit = async (data) => {
    if (isEditMode) {
      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          updatePlaceDetails(placeId, {
            PlaceName: data.PlaceName,
            PlaceORI: data.PlaceORI || null,
            PlaceDepartmentID: data.PlaceDepartmentID || null,
            PlaceType: data.PlaceType || null,
            IsDestination: data.IsDestination || false,
          }),
        onSuccess: () => {
          dispatch(notify('Place updated successfully', 'success'));
          onPlaceCreated?.();
        },
      });
      return;
    }

    const payload = {
      PlaceName: data.PlaceName,
      PlaceORI: data.PlaceORI || null,
      PlaceDepartmentID: data.PlaceDepartmentID || null,
      PlaceType: data.PlaceType || null,
      IsDestination: data.IsDestination || false,
      IsPrimary: data.IsPrimary || false,
      ptsAddressID: data.ptsAddressID || null,
      ContactMethod: data.ContactMethod || null,
      ContactMethodType: data.ContactMethodType || null,
      Info: data.Info || null,
      Notes: data.Notes || null,
      IsDefault: data.IsDefault || false,
      PhoneNumber: null,
      AreaCode: null,
      Extension: null,
    };

    await executeApiAction({
      setLoading: setSubmitting,
      dispatch,
      action: () => createPlace(payload),
      onSuccess: (place) => {
        dispatch(notify('Place created successfully', 'success'));
        onPlaceCreated?.(place);
      },
    });
  };

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this place. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  useEffect(() => {
    loadPlaceDetails();
  }, [loadPlaceDetails]);

  useEffect(() => {
    loadSopsCount();
  }, [loadSopsCount]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Place' : 'ADD PLACES'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting || loadingPlace}
      saveDisabled={submitting || !canSavePlace}
      maxWidth="lg"
      fullWidth
      closeOnBackdropClick={false}
      className="add-place-dialog"
      saveButton={{
        show: activeTab === PLACE_TAB_KEYS.EDIT,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{ show: false }}
      leftActions={<RMSRecordHistoryButton disabled />}>
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        className="add-place-dialog__tabs"
        variant="scrollable"
        scrollButtons="auto">
        {PLACE_TABS.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            label={renderTabLabel(tab)}
            disabled={tab.key !== PLACE_TAB_KEYS.EDIT && !isEditMode}
          />
        ))}
      </Tabs>

      {activeTab === PLACE_TAB_KEYS.ADDRESS && <PlaceAddressesTab ptsPlaceID={placeId} />}
      {activeTab === PLACE_TAB_KEYS.CONTACT && <PlaceContactsTab ptsPlaceID={placeId} />}
      {activeTab === PLACE_TAB_KEYS.EMPLOYEES && <PlaceEmployeesTab ptsPlaceID={placeId} />}
      {activeTab === PLACE_TAB_KEYS.SOPS && (
        <PlaceSopsTab ptsPlaceID={placeId} onSopsChanged={loadSopsCount} />
      )}
      {activeTab === PLACE_TAB_KEYS.VEHICLES && (
        <PlaceVehicleRegistrationsTab ptsPlaceID={placeId} />
      )}

      {activeTab === PLACE_TAB_KEYS.EDIT && (
        <FormProvider {...methods}>
          <Box className="rms-dialog-section" mt={2}>
            <SectionLabel>Place Details</SectionLabel>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <RMSTextField name="PlaceName" label="Place Name" inputProps={{ maxLength: 100 }} />
              </Grid>
              <Grid item xs={6}>
                <RMSSelect
                  name="PlaceType"
                  label="Place Type"
                  options={placeTypeOptions}
                  {...DICT_SELECT_PROPS}
                />
              </Grid>
              <Grid item xs={6}>
                <RMSTextField name="PlaceORI" label="Place ORI" inputProps={{ maxLength: 20 }} />
              </Grid>
              <Grid item xs={6}>
                <RMSTextField
                  name="PlaceDepartmentID"
                  label="Place Department ID"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12}>
                <RMSCheckbox name="IsDestination" label="Destination" />
              </Grid>
            </Grid>
          </Box>

          {!isEditMode && (
            <Box className="rms-dialog-section rms-dialog-section--last">
              <SectionLabel>Contact</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <SearchAddressAutoComplete
                    name="ptsAddressID"
                    label="Address Lookup"
                    defaultAddressId={ptsAddressID}
                    onAddIconClick={() => setAddAddressDialogOpen(true)}
                    showDetailsIcon={false}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSCheckbox name="IsPrimary" label="Primary" disabled={!ptsAddressID} />
                </Grid>
                <Grid item xs={6}>
                  <RMSSelect
                    name="ContactMethod"
                    label="Contact Method"
                    options={contactMethodOptions}
                    onChange={handleContactMethodChange}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={6}>
                  <RMSSelect
                    name="ContactMethodType"
                    label="Contact Type"
                    options={contactTypeOptions}
                    disabled={!ContactMethod}
                    {...DICT_SELECT_PROPS}
                  />
                </Grid>
                <Grid item xs={8}>
                  <RMSTextField
                    name="Info"
                    label="Info"
                    disabled={!ContactMethod}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <RMSCheckbox name="IsDefault" label="Default" disabled={!ContactMethod} />
                </Grid>
                <Grid item xs={12}>
                  <RMSTextField
                    name="Notes"
                    label="Notes"
                    multiline
                    rows={3}
                    inputProps={{ maxLength: 2000 }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </FormProvider>
      )}

      {addAddressDialogOpen && (
        <AddAddressDialog
          open={addAddressDialogOpen}
          onClose={() => setAddAddressDialogOpen(false)}
          onAddressCreated={handleAddressCreated}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default AddPlaceDialog;
